/**
 * Where Rôles & équipe is, from the URL hash, so every view is a link that can
 * be shared:
 *
 * - `#team` is the team and the roles × features grid;
 * - `#team?tab=who` is "who can do this", on the default action;
 * - `#team?tab=who&action=ISSUE_REFUND_STAFF_ACTION` on that one.
 */
import { ACTIONS, type ActionId } from "@wallyo/contracts/actions";

export const TEAM_TABS = ["roles", "who"] as const;
export type TeamTab = (typeof TEAM_TABS)[number];

/** The action "who can do this" opens on when none is named. */
export const DEFAULT_WHO_ACTION: ActionId = "ISSUE_REFUND_STAFF_ACTION";

export type TeamRoute = { tab: TeamTab; actionId: string };

export function parseTeamHash(hash: string): TeamRoute {
  const [, search = ""] = hash.replace(/^#\/?team/, "").split("?", 2);
  const params = new URLSearchParams(search);
  const asked = params.get("tab");
  let tab: TeamTab = "roles";
  if (asked === "who") tab = "who";
  const askedAction = params.get("action")?.trim() ?? "";
  // A link to an action nobody can be asked about (retired, a read, a
  // customer's) would leave the select on a value it doesn't offer.
  const known = ACTIONS.some(
    (action) =>
      action.actionId === askedAction &&
      action.actorType === "staff" &&
      action.effect === "write" &&
      action.status === "active",
  );
  let actionId: ActionId = DEFAULT_WHO_ACTION;
  if (known) actionId = askedAction as ActionId;
  return { tab, actionId };
}

export function teamHref(tab: TeamTab = "roles", actionId: string = DEFAULT_WHO_ACTION): string {
  const params = new URLSearchParams();
  if (tab !== "roles") params.set("tab", tab);
  if (tab === "who" && actionId !== DEFAULT_WHO_ACTION) params.set("action", actionId);
  const query = params.toString();
  return query ? `#team?${query}` : "#team";
}
